import React, { Fragment, memo, useContext } from 'react';
import PropTypes from 'prop-types';
import { bemGenerator } from '@prisjakt/ui-base';
import { AppContext } from '@prisjakt/ui-app-context';
import Breadcrumb from '@prisjakt/ui-breadcrumb';
import Grid from '@prisjakt/ui-grid';

import PageContent from '../../components/page-content';
import PageHeader from '../../components/page-header';
import WinnerCard from './components/winner-card';
import FeaturedArticle from '../landing-page/components/featured-article';

import './winners.scss';

const styles = bemGenerator('soty-winners', [
  'badge',
  'content',
  'main-award',
  'other-awards',
  'link',
  'legal',
]);

const ShopOfTheYearWinners = props => {
  const { t, market, linkTo } = useContext(AppContext);
  const { isMainPage } = props;
  const { year, categories, featuredArticle } = props.data.shopOfTheYear;
  const cdnDir = `${market.cdn}/g/store_of_the_year/${year}/${market.code}`;
  const badgeURI = `${cdnDir}/award/main_award.png`;

  const hasWinners = categories.nodes.every(category => category.winner);
  const mainAward = categories.nodes.find(category => category.mainAward);
  const otherAwards = categories.nodes.filter(category => !category.mainAward);

  return (
    <div className={styles.block}>
      <PageContent withAds>
        <Breadcrumb
          homeText={t('common', 'breadcrumb.home')}
          path={
            isMainPage
              ? [
                  {
                    pathName: linkTo('shopOfTheYear'),
                    name: t('campaigns', 'shop-of-the-year.title'),
                    id: '1',
                  },
                ]
              : [
                  {
                    pathName: linkTo('shopOfTheYear'),
                    name: t('campaigns', 'shop-of-the-year.title'),
                    id: '1',
                  },
                  {
                    pathName: linkTo('shopOfTheYearWinners', { year }),
                    name: t('campaigns', 'shop-of-the-year.winners-title', {
                      year,
                    }),
                    id: '2',
                  },
                ]
          }
        />

        <PageHeader
          media={<img className={styles.badge} src={badgeURI} alt="" />}
          title={t('campaigns', 'shop-of-the-year.winners-page-title', {
            year,
          })}
        >
          <p>
            {hasWinners
              ? t('campaigns', 'shop-of-the-year.winners-description', { year })
              : t('campaigns', 'shop-of-the-year.before-winners-description')}
          </p>
        </PageHeader>

        <div className={`column ${styles.content}`}>
          {hasWinners && (
            <Fragment>
              <div className={styles['main-award']}>
                <WinnerCard
                  award={mainAward}
                  winner={mainAward.winner}
                  featured
                />
              </div>

              {featuredArticle && (
                <FeaturedArticle
                  {...featuredArticle}
                  linkText={t('landing', 'featured-article.read-more')}
                />
              )}

              {otherAwards && otherAwards.length ? (
                <div className={styles['other-awards']}>
                  <Grid
                    blocks={{
                      xs: 2,
                      sm: 2,
                      md: 3,
                      lg: 4,
                      xl: 4,
                    }}
                  >
                    {otherAwards.map(category => (
                      <WinnerCard
                        key={category.id}
                        award={category}
                        winner={category.winner}
                      />
                    ))}
                  </Grid>
                </div>
              ) : null}
            </Fragment>
          )}

          <div className={styles.legal}>
            <p>{t('campaigns', 'shop-of-the-year.legal-information')}</p>
          </div>
        </div>
      </PageContent>
    </div>
  );
};

const { arrayOf, shape, string, number, bool } = PropTypes;

ShopOfTheYearWinners.propTypes = {
  data: shape({
    shopOfTheYear: shape({
      year: number.isRequired,
      categories: shape({
        nodes: arrayOf(
          shape({
            id: number.isRequired,
            name: string.isRequired,
            logo: string.isRequired,
            mainAward: bool.isRequired,
            winner: shape({
              name: string.isRequired,
              pathName: string.isRequired,
              hasLogo: bool.isRequired,
            }),
          })
        ).isRequired,
      }).isRequired,
    }),
  }).isRequired,
  isMainPage: bool,
};

ShopOfTheYearWinners.defaultProps = {
  isMainPage: false,
};

export default memo(ShopOfTheYearWinners);
